import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Lock,
  Gauge,
  Calendar,
  Play,
  Square,
  Crown,
  MapPin,
  Sparkles,
  FileDown,
  Camera,
  Receipt,
  Edit2,
  X,
  Wallet,
  Image as ImageIcon,
  Bell,
  Clock,
  History,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Fuel,
  Navigation,
} from "lucide-react";
import ActiveTrackBanner from "@/components/ActiveTrackBanner";
import ParkingCard, { ManualParkingSaveButton } from "@/components/ParkingCard";
import { AuthImage } from "@/components/AuthImage";

/** Pulls a HUMAN-READABLE error string out of an axios error.
 *
 *  As of iter18, several endpoints (Profit Radar 402, Receipt Vault 403)
 *  return `detail` as a STRUCTURED OBJECT (e.g. `{premium_required, message,
 *  product_id}`) instead of a plain string. Passing such an object directly
 *  to react-hot-toast crashes the page with "Objects are not valid as a
 *  React child". This helper handles both shapes safely. */
const detailMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return String(d.message);
  if (e?.message) return e.message;
  return fallback;
};

const PURPOSES = [
  { value: "business", label: "Business" },
  { value: "personal", label: "Personal" },
  { value: "medical", label: "Medical" },
  { value: "charity", label: "Charity" },
  { value: "moving", label: "Moving" },
];

const PERIODS = [
  { key: "daily", label: "Today" },
  { key: "weekly", label: "Week" },
  { key: "monthly", label: "Month" },
  { key: "yearly", label: "Year" },
];

/** Receipt categories. Kept in lockstep with backend `RECEIPT_CATEGORIES`
 *  in `/app/backend/routers/maintenance.py`. The `color` value drives the
 *  badge styling so each kind of expense is scannable in the list view. */
const RECEIPT_CATEGORIES = [
  { value: "oil_change",    label: "Oil Change",       color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { value: "repair",        label: "Repair",           color: "bg-red-500/15 text-red-300 border-red-500/30" },
  { value: "tires",         label: "Tires",            color: "bg-slate-500/15 text-slate-200 border-slate-500/40" },
  { value: "brakes",        label: "Brakes",           color: "bg-red-500/15 text-red-300 border-red-500/30" },
  { value: "battery",       label: "Battery",          color: "bg-green-500/15 text-green-300 border-green-500/30" },
  { value: "inspection",    label: "Inspection",       color: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
  { value: "car_wash",      label: "Car Wash",         color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  { value: "fuel",          label: "Fuel",             color: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  { value: "insurance",     label: "Insurance",        color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  { value: "registration",  label: "Registration",     color: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  { value: "parking_tolls", label: "Parking/Tolls",    color: "bg-teal-500/15 text-teal-300 border-teal-500/30" },
  { value: "other",         label: "Other",            color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
];
const CATEGORY_BY_VALUE = Object.fromEntries(RECEIPT_CATEGORIES.map((c) => [c.value, c]));
const RECEIPT_FILTERS = [
  { value: "all",        label: "All" },
  { value: "this_month", label: "This Month" },
  { value: "this_year",  label: "This Year" },
];

export default function Mileage() {
  const location = useLocation();
  const navigate = useNavigate();
  const safeBack = () => window.history.length > 2 ? navigate(-1) : navigate("/owner", { replace: true });
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({});
  const [sub, setSub] = useState({
    trial_active: false,
    trial_started: false,
    trial_days_remaining: 7,
    is_subscribed: false,
    tier: "free",
    has_auto_track: false,
    has_receipts: false,
    has_profit_radar: false,
    auto_track_enabled: false,
  });

  // Odometer form
  const [showOdoForm, setShowOdoForm] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [odo, setOdo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Quick trip (free, foreground only)
  const [tripActive, setTripActive] = useState(false);
  const [tripStartTime, setTripStartTime] = useState(null);
  const [tripElapsed, setTripElapsed] = useState(0);
  const [tripPurpose, setTripPurpose] = useState("business");
  const [tripNote, setTripNote] = useState("");
  const [tripDistance, setTripDistance] = useState("0");
  const elapsedRef = useRef(null);

  // Feature entitlements come from /mileage/subscription-status, which also
  // accounts for paid Owner Premium and active trials. Car owners have ONE
  // paid tier (Premium $3.99/mo), so Auto-Track is gated on Premium only.
  const isPremium = sub.tier === "premium";
  const hasAutoTrackEntitlement = sub.has_auto_track === true && isPremium;
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Reporting period + tax export state
  const [period, setPeriod] = useState("monthly");
  const [periodStats, setPeriodStats] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStart, setExportStart] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [exportEnd, setExportEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [exportIncludeMaint, setExportIncludeMaint] = useState(true);
  const [exporting, setExporting] = useState(false);

  // ---------- Service reminder state ----------
  const [serviceStatus, setServiceStatus] = useState(null);
  const [maintSettingsOpen, setMaintSettingsOpen] = useState(false);
  const [maintInterval, setMaintInterval] = useState(5000);
  const [maintRemindersOn, setMaintRemindersOn] = useState(true);

  // ---------- Reminder Notification Control state ----------
  // Active reminders surfaced on the page. Dismissed / snoozed ones live in
  // history. `exitingIds` drives the fade-out animation — IDs land here for
  // ~250ms after dismiss/snooze before we actually drop them from `reminders`.
  const [reminders, setReminders] = useState([]);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exitingIds, setExitingIds] = useState(new Set());

  // ---------- Receipt Vault state ----------
  // Top-level tab selector — keeps existing mileage flows untouched when the
  // user is on the "trips" tab and lazy-loads receipts only when needed.
  const [tab, setTab] = useState(location.state?.tab || "trips"); // "trips" | "receipts" | "profit_radar"
  const [receipts, setReceipts] = useState([]);
  const [receiptStats, setReceiptStats] = useState({ count: 0, total: 0, this_month: 0, this_year: 0, by_category: {} });
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptFilter, setReceiptFilter] = useState("all"); // "all"|"this_month"|"this_year"
  const [receiptCategoryFilter, setReceiptCategoryFilter] = useState(""); // "" = all categories
  const [receiptFormOpen, setReceiptFormOpen] = useState(false);
  const [receiptEditing, setReceiptEditing] = useState(null); // {receipt_id, ...} when editing
  const [receiptDetail, setReceiptDetail] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, mRes, svcRes, remRes] = await Promise.all([
        api.get("/mileage/subscription-status"),
        api.get("/mileage"),
        api.get("/mileage/service-status").catch(() => null),
        api.get("/mileage/reminders").catch(() => null),
      ]);
      setSub(sRes.data);
      setEntries(mRes.data.entries || []);
      setTrips(mRes.data.trips || []);
      setStats(mRes.data.stats || {});
      if (svcRes?.data) {
        setServiceStatus(svcRes.data);
        setMaintInterval(svcRes.data.interval || 5000);
        setMaintRemindersOn(svcRes.data.reminders_enabled !== false);
      }
      if (remRes?.data) setReminders(remRes.data.reminders || []);
    } catch (e) {
      toast.error(detailMsg(e, "Failed to load mileage"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // ─── Auto-restart background tracker if auto-track was already enabled ──
  useEffect(() => {
    if (sub.auto_track_enabled && hasAutoTrackEntitlement) {
      import("@/lib/backgroundTracker")
        .then(({ startBackgroundTracker }) => startBackgroundTracker())
        .catch((e) => console.warn("[Mileage] Background tracker auto-start failed:", e));
    }
  }, [sub.auto_track_enabled, hasAutoTrackEntitlement]);

  // Reload period stats whenever the period tab changes
  useEffect(() => {
    api.get(`/mileage/stats?period=${period}`)
      .then((r) => setPeriodStats(r.data))
      .catch(() => setPeriodStats(null));
  }, [period, trips.length]);

  const exportPdf = async () => {
    if (!exportStart || !exportEnd) {
      toast.error("Please select a date range.");
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams({
        start_date: exportStart,
        end_date: exportEnd,
        include_maintenance: exportIncludeMaint ? "true" : "false",
      });
      const res = await api.get(`/mileage/export-pdf?${params}`, { responseType: "blob" });
      const filename = `ShiftQuote_Tax_Report_${exportStart}_to_${exportEnd}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });

      // Native (Capacitor) — save to Documents then share
      try {
        const { Filesystem, Directory } = await import("@capacitor/filesystem");
        const { Share } = await import("@capacitor/share");
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64 = reader.result.split(",")[1];
          await Filesystem.writeFile({
            path: filename,
            data: base64,
            directory: Directory.Documents,
            recursive: true,
          });
          const uriResult = await Filesystem.getUri({ path: filename, directory: Directory.Documents });
          await Share.share({ title: "ShiftQuote Report", url: uriResult.uri, dialogTitle: "Save or Share PDF Report" });
          toast.success("PDF saved to Documents & ready to share");
        };
      } catch {
        // Web fallback
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PDF downloaded");
      }
      setExportOpen(false);
    } catch (err) {
      toast.error("Export failed. Please try again.");
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const updateTripPurpose = async (trip_id, newPurpose) => {
    try {
      await api.patch(`/mileage/trip/${trip_id}`, { purpose: newPurpose });
      await load();
    } catch (e) {
      toast.error(detailMsg(e, "Update failed"));
    }
  };

  // Trip timer
  useEffect(() => {
    if (!tripActive) {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
      return;
    }
    elapsedRef.current = setInterval(() => {
      setTripElapsed(Math.floor((Date.now() - tripStartTime) / 1000));
    }, 1000);
    return () => clearInterval(elapsedRef.current);
  }, [tripActive, tripStartTime]);

  // ---------- handlers ----------

  const submitOdo = async (e) => {
    e?.preventDefault?.();
    const value = parseInt(odo, 10);
    if (!value || value < 0) { toast.error("Enter a valid odometer reading"); return; }
    setSaving(true);
    try {
      await api.post("/mileage", { date, odometer_reading: value, note: note || null });
      toast.success("Entry added");
      setOdo(""); setNote(""); setShowOdoForm(false);
      await load();
    } catch (err) {
      toast.error(detailMsg(err, "Failed to add entry"));
    } finally {
      setSaving(false);
    }
  };

  const removeOdo = async (id) => {
    if (!window.confirm("Delete this odometer entry?")) return;
    try {
      await api.delete(`/mileage/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(detailMsg(e, "Delete failed")); }
  };

  // ---------- Service reminder handlers ----------
  // Called from the "I just got a service" button. Confirms with the user,
  // resets the counter on the server, and refreshes the status card.
  const resetService = async () => {
    if (!window.confirm("Reset your service counter? This will start tracking miles until your next maintenance reminder.")) return;
    try {
      const r = await api.patch("/mileage/reset-service");
      setServiceStatus(r.data);
      toast.success(`Service counter reset! We'll remind you in ${(r.data.interval || 5000).toLocaleString()} miles.`);
    } catch (e) {
      toast.error(detailMsg(e, "Failed to reset"));
    }
  };

  const saveMaintSettings = async () => {
    try {
      const r = await api.patch("/mileage/maintenance-settings", {
        interval_miles: parseInt(maintInterval, 10),
        enabled: maintRemindersOn,
      });
      setServiceStatus(r.data);
      setMaintSettingsOpen(false);
      toast.success("Maintenance preferences saved");
    } catch (e) {
      toast.error(detailMsg(e, "Failed to save"));
    }
  };

  // ---------- Reminder card handlers ----------
  // Animation flow: mark the reminder as "exiting" so the card fades out
  // (CSS handles the transition), then drop it from state after 250ms.
  const animateOutAndRemove = (id) => {
    setExitingIds((s) => new Set(s).add(id));
    setTimeout(() => {
      setReminders((arr) => arr.filter((r) => r.reminder_id !== id));
      setExitingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }, 250);
  };

  const dismissReminder = async (id) => {
    try {
      await api.post(`/mileage/reminders/${id}/dismiss`);
      toast.success("Reminder dismissed");
      animateOutAndRemove(id);
    } catch (e) {
      toast.error(detailMsg(e, "Failed to dismiss"));
    }
  };

  const snoozeReminder = async (id) => {
    try {
      await api.post(`/mileage/reminders/${id}/snooze`, {});
      toast.success("Snoozed for 500 mi / 3 days");
      animateOutAndRemove(id);
    } catch (e) {
      toast.error(detailMsg(e, "Failed to snooze"));
    }
  };

  const openHistory = async () => {
    try {
      const r = await api.get("/mileage/reminders/history");
      setReminderHistory(r.data?.reminders || []);
      setHistoryOpen(true);
    } catch (e) {
      toast.error(detailMsg(e, "Failed to load history"));
    }
  };

  // Loaded lazily on first visit to the Receipts tab and then refreshed
  // after every CRUD action. Two independent requests so the stats card
  // doesn't flash empty while the list re-fetches.
  const loadReceipts = async () => {
    // Defense-in-depth: never even attempt the fetch if the user isn't on a
    // tier that has Receipt access. Without this guard, free/pro users
    // hitting the Receipts tab would noisily 403 in the logs (and prior to
    // detailMsg, the 403's object-shaped detail would crash the page).
    if (!sub?.has_receipts) return;
    setReceiptsLoading(true);
    try {
      const params = new URLSearchParams();
      if (receiptCategoryFilter) params.set("category", receiptCategoryFilter);
      if (receiptFilter === "this_month") {
        const now = new Date();
        params.set("start_date", `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
      } else if (receiptFilter === "this_year") {
        params.set("start_date", `${new Date().getFullYear()}-01-01`);
      }
      const [rRes, sRes] = await Promise.all([
        api.get(`/maintenance/receipts?${params.toString()}`),
        api.get("/maintenance/stats"),
      ]);
      setReceipts(rRes.data?.receipts || []);
      setReceiptStats(sRes.data || {});
    } catch (e) {
      toast.error(detailMsg(e, "Failed to load receipts"));
    } finally {
      setReceiptsLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "receipts") return;
    loadReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, receiptFilter, receiptCategoryFilter, sub.has_receipts]);

  // Two-step upload: (1) POST /api/upload to get a storage_path, (2) POST
  // /api/maintenance/receipts referencing that path. The detached file
  // record stays linked to this user so the backend can verify ownership.
  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data?.path;
  };

  const saveReceipt = async (payload) => {
    if (receiptEditing) {
      await api.put(`/maintenance/receipts/${receiptEditing.receipt_id}`, payload);
      toast.success("Receipt updated");
    } else {
      await api.post("/maintenance/receipts", payload);
      toast.success("Receipt saved");
    }
    setReceiptFormOpen(false);
    setReceiptEditing(null);
    await loadReceipts();
  };

  const deleteReceipt = async (receipt_id) => {
    if (!window.confirm("Delete this receipt? The photo will also be removed.")) return;
    try {
      await api.delete(`/maintenance/receipts/${receipt_id}`);
      toast.success("Receipt deleted");
      setReceiptDetail(null);
      await loadReceipts();
    } catch (e) { toast.error(detailMsg(e, "Delete failed")); }
  };

  const startTrip = () => {
    if (tripActive) return;
    setTripStartTime(Date.now());
    setTripElapsed(0);
    setTripActive(true);
    toast.info("Trip started. Keep the app open during the trip.");
  };

  const stopTrip = async () => {
    if (!tripActive) return;
    const endTime = Date.now();
    setTripActive(false);
    let distance = parseFloat(tripDistance || "0");
    if (!distance || distance < 0) {
      toast.error("Enter a valid distance");
      // Allow user to retry — keep the timer paused
      setTripActive(false);
      return;
    }
    try {
      await api.post("/mileage/trip", {
        start_time: new Date(tripStartTime).toISOString(),
        end_time: new Date(endTime).toISOString(),
        distance_miles: distance,
        note: tripNote || null,
        purpose: tripPurpose,
        auto_tracked: false,
      });
      toast.success(`Trip logged — ${distance} mi`);
      setTripNote(""); setTripDistance("0"); setTripPurpose("business");
      setTripStartTime(null); setTripElapsed(0);
      await load();
    } catch (e) {
      toast.error(detailMsg(e, "Failed to log trip"));
    }
  };

  const removeTrip = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    try {
      await api.delete(`/mileage/trip/${id}`);
      toast.success("Trip deleted");
      await load();
    } catch (e) { toast.error(detailMsg(e, "Delete failed")); }
  };

  const toggleAutoTrack = async () => {
    // The backend remains the final authority; do not turn on GPS for free accounts.
    // Always allow disabling a tracker that was left on after access expired.
    if (!hasAutoTrackEntitlement && !sub.auto_track_enabled) {
      navigate("/owner/upgrade");
      return;
    }
    const enabling = !sub.auto_track_enabled;

    // ─── Permission Check (Always-On Location) ───────────────────────────
    // When enabling, we MUST have background ("Always") location permission.
    // Without it, the tracker silently fails when the app is backgrounded.
    if (enabling) {
      try {
        const { requestAlwaysOnLocation } = await import("@/lib/locationPermissions");
        const { status } = await requestAlwaysOnLocation();

        if (status === "denied") {
          toast.error("Location permission is required for automatic tracking.", { duration: 5000 });
          // Revert toggle — don't enable without permission
          return;
        }

        if (status === "limited") {
          // Settings was already opened by locationPermissions.js.
          // Show a non-blocking nudge so the user knows exactly what to do there.
          toast.info(
            "Please go to: Settings → Apps → ShiftQuote → Permissions → Location, select \"Allow all the time\", then come back and toggle Auto-Track again.",
            { duration: 9000 }
          );
          return;
        }

        if (status === "unavailable") {
          // Web — skip permission check, proceed to backend toggle only
        }
        // status === "granted" — proceed normally
      } catch (e) {
        console.warn("[toggleAutoTrack] Permission check failed:", e);
        // Non-blocking — allow backend toggle even if native check fails (web)
      }
    }

    // ─── Android Battery Optimization Warning ─────────────────────────────
    if (enabling) {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.getPlatform() === "android") {
          toast.info(
            "Samsung tip: go to Settings → Apps → ShiftQuote → Battery → set to Unrestricted, and Location → Allow all the time. This prevents Android from killing the tracker.",
            { duration: 10000 }
          );
        }
      } catch (_) { /* ignore on web */ }
    }

    try {
      const r = await api.post("/mileage/auto-track", { enabled: enabling });
      setSub((s) => ({
        ...s,
        auto_track_enabled: r.data.enabled,
        trial_started: r.data.trial_started ?? s.trial_started,
        trial_active: r.data.trial_active ?? s.trial_active,
        trial_days_remaining: r.data.trial_days_remaining ?? s.trial_days_remaining,
        has_auto_track: r.data.has_auto_track ?? s.has_auto_track,
        has_receipts: r.data.has_receipts ?? s.has_receipts,
        has_profit_radar: r.data.has_profit_radar ?? s.has_profit_radar,
        tier: r.data.tier ?? s.tier,
      }));

      if (enabling) {
        // ─── Start Background Tracker ──────────────────────────────────────
        try {
          const { startBackgroundTracker } = await import("@/lib/backgroundTracker");
          await startBackgroundTracker();
          toast.success(
            (sub.is_subscribed || isPremium)
              ? "Auto-Track enabled. Background mileage tracking is now active."
              : `Auto-Track enabled. ${r.data.trial_days_remaining || 7} trial day${(r.data.trial_days_remaining || 7) === 1 ? "" : "s"} remaining.`,
            { duration: 6000 }
          );
        } catch (e) {
          console.warn("[toggleAutoTrack] Background tracker start failed:", e);
          toast.error(
            "Auto-Track enabled but GPS service failed to start. Please check Location permission (Allow all the time) and disable battery optimization for this app.",
            { duration: 8000 }
          );
        }
      } else {
        // ─── Stop Background Tracker ───────────────────────────────────────
        try {
          const { stopBackgroundTracker } = await import("@/lib/backgroundTracker");
          await stopBackgroundTracker();
        } catch (e) {
          console.warn("[toggleAutoTrack] Background tracker stop failed:", e);
        }
        toast.info("Auto-Track disabled. Manual logging remains free.");
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywallOpen(true);
      } else {
        toast.error(detailMsg(e, "Failed"));
      }
    }
  };

  const onSubscribe = () => {
    setPaywallOpen(false);
    navigate("/owner/upgrade");
  };

  const fmtElapsed = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":");
  };

  if (loading) {
    return <div className="min-h-screen bg-[#09090B] text-zinc-500 flex items-center justify-center label-eyebrow">LOADING...</div>;
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar />
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => safeBack()} className="flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="mileage-back-btn">
            <ChevronLeft className="w-5 h-5" /> Back
          </button>
          <div className="font-display text-sm tracking-widest text-zinc-500">MILEAGE TRACKER</div>
          <div className="w-12" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Active Track Banner — visible on all tabs when tracking */}
        <ActiveTrackBanner onTap={() => setTab("trips")} />
        {/* Tab bar — Trips (existing flows) vs Receipts (new Vault).
            Kept as a single Mileage page so the user has one Tax Companion
            destination, not two scattered ones. */}
        <div className="flex border-b border-white/10 -mt-2 overflow-hidden" data-testid="mileage-tabs">
          <button
            onClick={() => setTab("trips")}
            className={`relative flex-1 min-w-0 flex items-center justify-center px-2 py-3 text-xs font-display tracking-widest uppercase transition-colors ${tab === "trips" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
            data-testid="tab-trips"
          >
            <span className="truncate">Trips</span>
            {tab === "trips" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#FF3B30]" />}
          </button>
          <button
            onClick={() => setTab("receipts")}
            className={`relative flex-1 min-w-0 flex items-center justify-center px-2 py-3 text-xs font-display tracking-widest uppercase transition-colors gap-1 ${tab === "receipts" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
            data-testid="tab-receipts"
          >
            <Receipt className="w-3 h-3" />
            <span className="truncate">Receipts</span>
            {!sub.has_receipts && <Lock className="w-3 h-3 text-[#FFB800]" aria-label="Premium required" />}
            {receiptStats.count > 0 && (
              <span className="text-[10px] font-mono bg-zinc-800 text-zinc-300 px-1.5 py-0.5">{receiptStats.count}</span>
            )}
            {tab === "receipts" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#FF3B30]" />}
          </button>
          <button
            onClick={() => setTab("profit_radar")}
            className={`relative flex-1 min-w-0 flex items-center justify-center px-2 py-3 text-xs font-display tracking-widest uppercase transition-colors gap-1 ${tab === "profit_radar" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
            data-testid="tab-profit-radar"
          >
            <TrendingUp className="w-3 h-3" />
            <span className="hidden sm:inline truncate">Profit Radar</span>
            <span className="sm:hidden truncate">Profit</span>
            {!sub.has_profit_radar && <Lock className="w-3 h-3 text-[#FFB800]" aria-label="Premium required" />}
            {tab === "profit_radar" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#FF3B30]" />}
          </button>
        </div>

        {tab === "trips" && (<>
        {/* Parking Card (auto-expires after 24h) */}
        <ParkingCard />

        {/* Manual Save Parking button for non-Auto-Track users */}
        <ManualParkingSaveButton />

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="This Week" value={`${(stats.miles_this_week || 0).toLocaleString()} mi`} />
          <StatCard label="This Month" value={`${(stats.miles_this_month || 0).toLocaleString()} mi`} />
          <StatCard label="This Year" value={`${(stats.miles_this_year || 0).toLocaleString()} mi`} />
          <StatCard
            label="Since Last Service"
            value={stats.miles_since_last_service != null ? `${stats.miles_since_last_service.toLocaleString()} mi` : "—"}
          />
        </div>

        {/* Active maintenance reminders — Dismiss/Snooze actions per card.
            Sits ABOVE the ServiceStatusCard so the user's eye lands on the
            actionable nudge first. The card fades out via the exitingIds
            set when dismissed or snoozed for ~250ms before being removed
            from state, so the transition feels intentional. */}
        {reminders.length > 0 && (
          <div className="space-y-2" data-testid="reminders-section">
            {reminders.map((r) => (
              <ReminderCard
                key={r.reminder_id}
                reminder={r}
                exiting={exitingIds.has(r.reminder_id)}
                onDismiss={() => dismissReminder(r.reminder_id)}
                onSnooze={() => snoozeReminder(r.reminder_id)}
                onOpenLink={() => navigate("/post")}
              />
            ))}
            <button
              onClick={openHistory}
              className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 transition-colors"
              data-testid="reminders-history-btn"
            >
              <History className="w-3 h-3" />
              View reminder history
            </button>
          </div>
        )}

        {/* Service status — drives the maintenance-reminder engagement loop.
            Tap the reset button after you've actually had the car serviced
            so the next interval crossing fires a fresh push notification. */}
        {serviceStatus && (
          <ServiceStatusCard
            status={serviceStatus}
            onReset={resetService}
            onOpenSettings={() => setMaintSettingsOpen(true)}
          />
        )}

        {/* Quick Trip — free */}
        <div className="border border-white/10 bg-[#121214] p-6" data-testid="trip-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="label-eyebrow text-zinc-500">QUICK TRIP · FREE</div>
              <div className="font-display text-xl mt-1">{tripActive ? "Trip in progress" : "Log a trip"}</div>
            </div>
            {tripActive ? (
              <div className="font-mono text-3xl tabular-nums" data-testid="trip-timer">
                {fmtElapsed(tripElapsed)}
              </div>
            ) : (
              <button
                onClick={startTrip}
                className="px-5 py-2.5 bg-[#34C759] text-black font-display tracking-wide flex items-center gap-2"
                data-testid="trip-start-btn"
              >
                <Play className="w-4 h-4" /> START
              </button>
            )}
          </div>

          {tripActive && (
            <div className="space-y-3 mt-4">
              <div>
                <label className="label-eyebrow text-zinc-500">DISTANCE (MI)</label>
                <input type="number" step="0.1" value={tripDistance} onChange={(e) => setTripDistance(e.target.value)}
                       placeholder="37.0" className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="trip-distance" />
              </div>
              <div>
                <label className="label-eyebrow text-zinc-500">PURPOSE</label>
                <select
                  value={tripPurpose}
                  onChange={(e) => setTripPurpose(e.target.value)}
                  className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white"
                  data-testid="trip-purpose"
                >
                  {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <div className="text-xs text-zinc-600 mt-1">For IRS tax-deduction reporting. Default is Business.</div>
              </div>
              <div>
                <label className="label-eyebrow text-zinc-500">NOTE</label>
                <input type="text" value={tripNote} onChange={(e) => setTripNote(e.target.value)}
                       placeholder='e.g., "commute", "road trip"' className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="trip-note" maxLength={200} />
              </div>
              <button onClick={stopTrip} className="w-full py-3 bg-[#FF3B30] text-white font-display tracking-wide flex items-center justify-center gap-2" data-testid="trip-stop-btn">
                <Square className="w-4 h-4" /> STOP & SAVE
              </button>
            </div>
          )}
        </div>

        {/* Auto-Track section (gated) */}
        <div className="border border-white/10 bg-gradient-to-br from-[#FFB800]/8 to-transparent p-6" data-testid="autotrack-section">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-[240px]">
              <div className="w-10 h-10 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#FFB800]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display text-xl">Auto-Track</div>
                  <span className="px-1.5 py-0.5 text-[10px] font-mono bg-[#FFB800]/20 text-[#FFB800]">PREMIUM</span>
                  {!hasAutoTrackEntitlement && <Lock className="w-4 h-4 text-[#FFB800]" aria-label="Premium required" />}
                </div>
                <div className="text-sm text-zinc-400 mt-1 max-w-md">
                  Records your miles in the background, even when the app is closed.
                </div>
                {sub.trial_active && !sub.is_subscribed && (
                  <div className="text-xs text-[#FFB800] mt-2" data-testid="autotrack-trial-status">
                    Trial: {sub.trial_days_remaining} day{sub.trial_days_remaining === 1 ? "" : "s"} remaining
                  </div>
                )}
                {sub.is_subscribed && hasAutoTrackEntitlement && (
                  <div className="text-xs text-[#34C759] mt-2 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Subscribed
                  </div>
                )}
              </div>
            </div>
            {hasAutoTrackEntitlement || sub.auto_track_enabled ? (
              <button
                onClick={toggleAutoTrack}
                className={`relative h-7 w-12 rounded-full transition-colors ${sub.auto_track_enabled ? "bg-[#FFB800]" : "bg-zinc-700"}`}
                data-testid="autotrack-toggle"
                aria-label="Toggle Auto-Track"
              >
                <span
                  className={`absolute top-0.5 ${sub.auto_track_enabled ? "left-6" : "left-0.5"} w-6 h-6 bg-white rounded-full transition-all`}
                />
              </button>
            ) : (
              <Link to="/owner/upgrade" className="inline-flex items-center gap-2 px-4 py-2 border border-[#FFB800]/50 text-[#FFB800] text-xs font-display tracking-wide hover:bg-[#FFB800]/10" data-testid="autotrack-upgrade">
                <Lock className="w-4 h-4" /> Upgrade to Premium
              </Link>
            )}
          </div>
          {sub.auto_track_enabled && (
            <div className="mt-4 text-xs text-zinc-500 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Background GPS rollout in the next app update — your preference is saved.
            </div>
          )}
        </div>

        {/* Manual odometer */}
        <div>
          <button
            onClick={() => setShowOdoForm((v) => !v)}
            className="w-full border border-dashed border-white/20 bg-[#121214] hover:border-white/40 px-6 py-4 flex items-center justify-center gap-2 text-zinc-300 transition-colors"
            data-testid="mileage-add-btn"
          >
            <Plus className="w-4 h-4" /> {showOdoForm ? "Cancel" : "Add odometer reading"}
          </button>
          {showOdoForm && (
            <form onSubmit={submitOdo} className="bg-[#121214] border border-white/10 p-6 mt-3 space-y-4" data-testid="mileage-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow text-zinc-500">DATE</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                         max={new Date().toISOString().slice(0, 10)}
                         className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="mileage-date-input" required />
                </div>
                <div>
                  <label className="label-eyebrow text-zinc-500">ODOMETER (MILES)</label>
                  <input type="number" inputMode="numeric" placeholder="48,250" value={odo} onChange={(e) => setOdo(e.target.value)}
                         className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="mileage-odo-input" min="0" required />
                </div>
              </div>
              <div>
                <label className="label-eyebrow text-zinc-500">NOTE (OPTIONAL)</label>
                <input type="text" placeholder='e.g., "oil change", "tune-up"' value={note} onChange={(e) => setNote(e.target.value)}
                       className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="mileage-note-input" maxLength={200} />
                <div className="text-xs text-zinc-600 mt-1">Tip: include &ldquo;service&rdquo; or &ldquo;oil&rdquo; in the note to track miles since last service.</div>
              </div>
              <button type="submit" disabled={saving} className="w-full py-3 bg-[#FF3B30] text-white font-display tracking-wide disabled:opacity-50" data-testid="mileage-submit-btn">
                {saving ? "SAVING..." : "ADD ENTRY"}
              </button>
            </form>
          )}
        </div>

        {/* Reporting: period tabs + Tax export */}
        <div className="border border-white/10 bg-[#121214] p-5" data-testid="reporting-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1 bg-[#09090B] p-1 border border-white/10" data-testid="period-tabs">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs font-display tracking-widest transition-colors ${period === p.key ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"}`}
                  data-testid={`period-${p.key}`}
                >
                  {p.label.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setExportIncludeMaint(sub.has_receipts === true); setExportOpen(true); }}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="export-csv-btn"
              title="Includes mileage log + maintenance receipts"
            >
              <FileDown className="w-4 h-4" /> Export Tax Report
            </button>
          </div>
          {periodStats && (
            <div className="mt-4 text-sm" data-testid="period-summary">
              <div className="font-display text-2xl">
                {periodStats.trip_count} trip{periodStats.trip_count === 1 ? "" : "s"}
                <span className="mx-2 text-zinc-600">·</span>
                {periodStats.total_miles.toLocaleString()} mi
                <span className="mx-2 text-zinc-600">·</span>
                {periodStats.total_hours}h driving
              </div>
              {periodStats.purpose_breakdown && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(periodStats.purpose_breakdown).map(([k, v]) => v > 0 && (
                    <span key={k} className="px-2 py-1 text-[10px] font-mono bg-[#09090B] border border-white/10">
                      {k.toUpperCase()} {v} mi
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* History */}
        {entries.length === 0 && trips.length === 0 ? (
          <div className="text-center py-12 text-zinc-500" data-testid="mileage-empty">
            <Gauge className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <div className="font-display text-xl text-white">Start tracking your mileage</div>
            <div className="text-sm mt-1">Log a trip with Start/Stop, or add an odometer reading above.</div>
          </div>
        ) : (
          <div className="space-y-6">
            {trips.length > 0 && (
              <div data-testid="trips-history">
                <div className="label-eyebrow text-zinc-500 mb-3">TRIPS · {trips.length}</div>
                <div className="space-y-2">
                  {trips.map((t) => (
                    <div key={t.trip_id} className="bg-[#121214] border border-white/10 p-4 flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm text-zinc-400 flex-wrap">
                          <Calendar className="w-3.5 h-3.5" /> {(t.end_time || "").slice(0, 10)}
                          {t.auto_tracked && (
                            <span className="px-1.5 py-0.5 text-[10px] font-mono bg-[#FFB800]/15 text-[#FFB800]">AUTO</span>
                          )}
                          <select
                            value={t.purpose || "business"}
                            onChange={(e) => updateTripPurpose(t.trip_id, e.target.value)}
                            className="bg-transparent border border-white/10 text-[10px] font-mono px-1.5 py-0.5 uppercase tracking-widest text-zinc-300"
                            data-testid={`trip-purpose-${t.trip_id}`}
                          >
                            {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        </div>
                        <div className="font-display text-2xl mt-1">
                          {Number(t.distance_miles || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          <span className="text-base text-zinc-500"> mi</span>
                        </div>
                        {t.note && <div className="text-sm text-zinc-400 mt-1 truncate">{t.note}</div>}
                      </div>
                      <button onClick={() => removeTrip(t.trip_id)} className="text-zinc-500 hover:text-[#FF3B30] p-2" aria-label="Delete trip">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {entries.length > 0 && (
              <div data-testid="mileage-history">
                <div className="label-eyebrow text-zinc-500 mb-3">ODOMETER LOG · {entries.length}</div>
                <div className="space-y-2">
                  {entries.map((e) => (
                    <div key={e.entry_id} className="bg-[#121214] border border-white/10 p-4 flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                          <Calendar className="w-3.5 h-3.5" /> {e.date}
                          {e.delta > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-[#34C759]/10 text-[#34C759] text-xs font-display tracking-wide">
                              +{e.delta.toLocaleString()} mi
                            </span>
                          )}
                        </div>
                        <div className="font-display text-2xl mt-1">{e.odometer_reading.toLocaleString()} <span className="text-base text-zinc-500">mi</span></div>
                        {e.note && <div className="text-sm text-zinc-400 mt-1 truncate">{e.note}</div>}
                      </div>
                      <button onClick={() => removeOdo(e.entry_id)} className="text-zinc-500 hover:text-[#FF3B30] p-2"
                              data-testid={`mileage-delete-${e.entry_id}`} aria-label="Delete entry">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </>)}

        {tab === "receipts" && (
          !sub.has_receipts ? (
            <PremiumPaywall
              testId="receipts-paywall"
              icon={Receipt}
              title="Keep every receipt in one place."
              body="No more shoeboxes full of gas station slips — snap it, store it, export it at tax time."
              priceLabel="$3.99/mo"
            />
          ) : (
            <ReceiptsTab
              receipts={receipts}
              stats={receiptStats}
              loading={receiptsLoading}
              filter={receiptFilter}
              setFilter={setReceiptFilter}
              categoryFilter={receiptCategoryFilter}
              setCategoryFilter={setReceiptCategoryFilter}
              onAdd={() => { setReceiptEditing(null); setReceiptFormOpen(true); }}
              onOpenDetail={(r) => setReceiptDetail(r)}
              onExport={() => { setExportIncludeMaint(true); setExportOpen(true); }}
            />
          )
        )}
        {tab === "profit_radar" && (
          !sub.has_profit_radar ? (
            <PremiumPaywall
              testId="profit-radar-paywall"
              icon={TrendingUp}
              title="See what you REALLY make."
              body="Most drivers don't realize how much gas and taxes eat into their earnings. Profit Radar shows you the estimate truth — no sugarcoating."
              priceLabel="$3.99/mo"
            />
          ) : (
            <ProfitRadarTab />
          )
        )}
      </div>

      {/* Export-for-Taxes modal */}
      {exportOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="export-modal">
          <div className="bg-[#121214] border border-white/10 w-full max-w-md p-6 sm:p-8 space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="label-eyebrow text-zinc-500">EXPORT TAX REPORT</div>
                <button onClick={() => setExportOpen(false)} className="text-zinc-500 hover:text-white" data-testid="export-close-btn"><X className="w-5 h-5" /></button>
              </div>
              <h2 className="font-display text-2xl mt-1">{exportIncludeMaint ? "Download Tax Report" : "Download Mileage Log"}</h2>
              <p className="text-sm text-zinc-400 mt-2">
                {exportIncludeMaint
                  ? "Includes your mileage log + maintenance receipts in a single CSV with section banners. IRS-compatible columns."
                  : "CSV columns: Date, Start Location, End Location, Miles, Purpose, Notes. Compliant with IRS Publication 463."}
              </p>
            </div>
            <label className="flex items-start gap-3 text-sm text-zinc-200 cursor-pointer select-none" data-testid="export-include-maintenance-row">
              <input
                type="checkbox"
                checked={exportIncludeMaint}
                disabled={!sub.has_receipts}
                onChange={(e) => setExportIncludeMaint(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FF3B30]"
                data-testid="export-include-maintenance"
              />
              <span>
                Include maintenance receipts {!sub.has_receipts && <Lock className="w-3 h-3 inline text-[#FFB800]" aria-label="Premium required" />}
                <span className="block text-xs text-zinc-500 mt-0.5">{sub.has_receipts ? "Adds your Receipt Vault entries as a second section in the same file." : "Premium required for receipt exports; mileage-only reports are free."}</span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow text-zinc-500">FROM</label>
                <input type="date" value={exportStart} onChange={(e) => setExportStart(e.target.value)}
                       className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="export-start" />
              </div>
              <div>
                <label className="label-eyebrow text-zinc-500">TO</label>
                <input type="date" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)}
                       className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="export-end" />
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
              <button onClick={() => setExportOpen(false)} className="flex-1 py-3 border border-white/10 text-zinc-300 font-display tracking-wide" disabled={exporting}>
                CANCEL
              </button>
              <button onClick={exportPdf} disabled={exporting} className="flex-1 py-3 bg-[#FF3B30] text-white font-display tracking-wide disabled:opacity-60" data-testid="export-download-btn">
                {exporting ? "PREPARING..." : "DOWNLOAD PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription paywall — Auto-Track only */}
      {paywallOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="autotrack-paywall">
          <div className="bg-[#121214] border border-[#FFB800]/30 w-full max-w-md p-6 sm:p-8 space-y-5">
            <div className="text-center">
              <Lock className="w-10 h-10 mx-auto text-[#FFB800] mb-3" />
              <div className="label-eyebrow text-[#FFB800] mb-2">AUTO-TRACK · PREMIUM</div>
              <h2 className="font-display text-2xl">Your Free Trial Has Ended</h2>
              <p className="text-sm text-zinc-400 mt-2">Subscribe to keep recording your miles in the background, even when the app is closed.</p>
              <div className="font-display text-4xl mt-5">$3.99/month</div>
              <div className="text-xs text-zinc-600 mt-1">Manual tracking stays free, always.</div>
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={onSubscribe} className="w-full py-3 bg-[#FFB800] text-black font-display tracking-wide" data-testid="autotrack-subscribe-btn">
                UPGRADE TO PREMIUM
              </button>
              <button onClick={() => setPaywallOpen(false)} className="w-full py-3 border border-white/10 text-zinc-300 font-display tracking-wide">
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt form (add / edit) */}
      {receiptFormOpen && (
        <ReceiptForm
          editing={receiptEditing}
          uploadPhoto={uploadPhoto}
          onSave={saveReceipt}
          onClose={() => { setReceiptFormOpen(false); setReceiptEditing(null); }}
        />
      )}

      {/* Reminder history modal — every reminder this user has ever had,
          including dismissed and snoozed ones with their badges. */}
      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="reminder-history-modal">
          <div className="bg-[#121214] border border-white/10 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#121214]">
              <div>
                <div className="label-eyebrow text-zinc-500">REMINDER HISTORY</div>
                <div className="font-display text-xl mt-1">All reminders</div>
              </div>
              <button onClick={() => setHistoryOpen(false)} className="text-zinc-500 hover:text-white p-1" data-testid="reminder-history-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-2">
              {reminderHistory.length === 0 ? (
                <div className="text-sm text-zinc-500 text-center py-8">No reminders yet.</div>
              ) : reminderHistory.map((r) => <ReminderHistoryRow key={r.reminder_id} reminder={r} />)}
            </div>
          </div>
        </div>
      )}

      {/* Maintenance settings modal */}
      {maintSettingsOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="maint-settings-modal">
          <div className="bg-[#121214] border border-white/10 w-full max-w-md p-6 sm:p-8 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow text-zinc-500">VEHICLE MAINTENANCE</div>
                <h2 className="font-display text-2xl mt-1">Reminder preferences</h2>
              </div>
              <button onClick={() => setMaintSettingsOpen(false)} className="text-zinc-500 hover:text-white p-1" data-testid="maint-settings-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="label-eyebrow text-zinc-500">SERVICE INTERVAL · MILES</label>
              <div className="flex flex-wrap gap-2 mt-2 mb-3" data-testid="maint-interval-presets">
                {[3000, 5000, 7500, 10000].map((p) => (
                  <button
                    key={p}
                    onClick={() => setMaintInterval(p)}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${maintInterval === p ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "border-white/10 text-zinc-400 hover:text-white"}`}
                    data-testid={`maint-preset-${p}`}
                  >
                    {p.toLocaleString()} mi
                  </button>
                ))}
              </div>
              <input
                type="number"
                inputMode="numeric"
                min={1000}
                max={25000}
                step={500}
                value={maintInterval}
                onChange={(e) => setMaintInterval(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-3 py-2 text-white"
                data-testid="maint-interval-input"
              />
              <div className="text-xs text-zinc-500 mt-1">Between 1,000 and 25,000 miles.</div>
            </div>

            <label className="flex items-start gap-3 text-sm text-zinc-200 cursor-pointer">
              <input
                type="checkbox"
                checked={maintRemindersOn}
                onChange={(e) => setMaintRemindersOn(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FF3B30]"
                data-testid="maint-reminders-toggle"
              />
              <span>
                Maintenance reminders
                <span className="block text-xs text-zinc-500 mt-0.5">Push notification when you've driven {maintInterval ? Number(maintInterval).toLocaleString() : "5,000"} miles since your last service.</span>
              </span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
              <button onClick={() => setMaintSettingsOpen(false)} className="flex-1 py-3 border border-white/10 text-zinc-300 font-display tracking-wide" data-testid="maint-settings-cancel">
                CANCEL
              </button>
              <button onClick={saveMaintSettings} className="flex-1 py-3 bg-[#FF3B30] text-white font-display tracking-wide" data-testid="maint-settings-save">
                SAVE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt detail view */}
      {receiptDetail && (
        <ReceiptDetailModal
          receipt={receiptDetail}
          onClose={() => setReceiptDetail(null)}
          onEdit={() => { setReceiptEditing(receiptDetail); setReceiptDetail(null); setReceiptFormOpen(true); }}
          onDelete={() => deleteReceipt(receiptDetail.receipt_id)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-4">
      <div className="label-eyebrow text-zinc-500">{label}</div>
      <div className="font-display text-xl mt-2">{value}</div>
    </div>
  );
}

/** Service-status mini-card. Renders the engagement loop progress bar:
 *  green < 80% → amber 80-99% → red overdue (>= 100%). Tapping the reset
 *  button confirms with the user, fires PATCH /mileage/reset-service,
 *  and arms a fresh reminder cycle. */
function ServiceStatusCard({ status, onReset, onOpenSettings }) {
  const pct = Math.min(100, Math.max(0, Number(status.percent) || 0));
  const overdue = status.is_overdue;
  // Color band — keeps the bar/label scannable at a glance.
  const tone = overdue
    ? { bar: "bg-[#FF3B30]", text: "text-[#FF3B30]", label: "OVERDUE" }
    : pct >= 80
      ? { bar: "bg-[#FF9500]", text: "text-[#FF9500]", label: "DUE SOON" }
      : { bar: "bg-[#34C759]", text: "text-[#34C759]", label: "ON TRACK" };
  const remindersOff = status.reminders_enabled === false;
  return (
    <div className="border border-white/10 bg-[#121214] p-5" data-testid="service-status-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-zinc-400" />
            <span className="label-eyebrow text-zinc-500">MILES SINCE LAST SERVICE</span>
            <span className={`text-[10px] font-mono uppercase tracking-widest ${tone.text}`} data-testid="service-status-band">
              {tone.label}
            </span>
            {remindersOff && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-white/10 px-1.5 py-0.5">REMINDERS OFF</span>
            )}
          </div>
          <div className="font-display text-2xl mt-2" data-testid="service-status-numbers">
            {Number(status.miles_since_service).toLocaleString()}
            <span className="text-base text-zinc-500"> / {Number(status.interval).toLocaleString()} mi</span>
          </div>
        </div>
        <button
          onClick={onOpenSettings}
          className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest border border-white/10 px-2 py-1"
          data-testid="service-settings-btn"
        >
          Settings
        </button>
      </div>

      {/* Progress bar — clamps the visual to 100% even when overdue so the
          bar fills the track fully (more visually impactful than overflow). */}
      <div className="mt-3 h-2 bg-[#09090B] border border-white/10 overflow-hidden" data-testid="service-progress-bar">
        <div
          className={`h-full ${tone.bar} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <button
        onClick={onReset}
        className="mt-4 w-full py-2.5 bg-[#34C759]/10 hover:bg-[#34C759]/20 border border-[#34C759]/30 text-[#34C759] font-display tracking-widest text-sm flex items-center justify-center gap-2 transition-colors"
        data-testid="reset-service-btn"
      >
        <Sparkles className="w-4 h-4" /> I JUST GOT A SERVICE
      </button>
      {overdue && (
        <div className="mt-3 text-xs text-[#FF3B30] flex items-start gap-2" data-testid="service-overdue-msg">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          You're {Number(status.miles_since_service - status.interval).toLocaleString()} miles past your interval. Tap below to get instant quotes from nearby shops.
        </div>
      )}
    </div>
  );
}

/** Maintenance reminder card with Dismiss + Snooze inline actions.
 *  Dismiss permanently removes the reminder (history-only after).
 *  Snooze pushes both a 500-mi AND a 3-day threshold to the backend —
 *  whichever fires first re-surfaces this same reminder. Tapping the card
 *  body (anywhere outside the buttons) deep-links to /post so the user
 *  drops into the marketplace loop with a pre-filled "Routine Maintenance"
 *  title (driven by the push payload).
 *
 *  `exiting` toggles the CSS opacity transition for ~250ms so the card
 *  visibly fades on dismiss/snooze before being unmounted by the parent.
 */
function ReminderCard({ reminder, exiting, onDismiss, onSnooze, onOpenLink }) {
  const miles = Math.round(Number(reminder.trigger_miles_since) || 0);
  return (
    <div
      className={`relative border border-[#FF9500]/40 bg-gradient-to-br from-[#FF9500]/12 to-[#FF3B30]/8 p-4 transition-all duration-250 ${exiting ? "opacity-0 -translate-x-3 pointer-events-none" : "opacity-100"}`}
      data-testid={`reminder-card-${reminder.reminder_id}`}
      style={{ transitionDuration: "250ms" }}
    >
      <button
        type="button"
        onClick={onOpenLink}
        className="text-left w-full"
        data-testid={`reminder-cta-${reminder.reminder_id}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-[#FF9500]/20 flex items-center justify-center shrink-0">
            <Bell className="w-4 h-4 text-[#FF9500]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#FF9500]">TIME FOR A SERVICE?</div>
            <p className="text-sm text-zinc-100 mt-1 leading-snug">
              You've driven <span className="font-display">{miles.toLocaleString()}</span> mi since your last service.
              Tap to get instant quotes from nearby shops.
            </p>
          </div>
        </div>
      </button>
      <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-white/10">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSnooze(); }}
          className="text-xs text-zinc-300 hover:text-white px-2 py-1 border border-white/10 hover:border-white/30 flex items-center gap-1.5 transition-colors"
          data-testid={`reminder-snooze-${reminder.reminder_id}`}
        >
          <Clock className="w-3 h-3" /> Snooze
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="text-xs text-zinc-500 hover:text-[#FF3B30] px-2 py-1 flex items-center gap-1.5 transition-colors"
          data-testid={`reminder-dismiss-${reminder.reminder_id}`}
        >
          <X className="w-3 h-3" /> Dismiss
        </button>
      </div>
    </div>
  );
}

/** One row in the Reminder History modal. Renders a state badge (Active /
 *  Snoozed / Dismissed / Resolved) so the user can see what happened to
 *  each past nudge. */
function ReminderHistoryRow({ reminder }) {
  const r = reminder;
  // Pick the most representative label/color based on the record's flags.
  // Order matters: resolved beats dismissed beats snoozed beats active.
  let badge;
  if (r.resolved_at) badge = { label: "Resolved", cls: "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30" };
  else if (r.dismissed) badge = { label: "Dismissed", cls: "bg-zinc-700/30 text-zinc-300 border-zinc-600" };
  else if (r.snoozed) badge = { label: "Snoozed", cls: "bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/30" };
  else badge = { label: "Active", cls: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30" };
  const created = r.created_at ? r.created_at.slice(0, 10) : "—";
  return (
    <div className="border border-white/10 bg-[#09090B] p-3" data-testid={`history-row-${r.reminder_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Calendar className="w-3 h-3" /> {created}
          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border font-mono ${badge.cls}`} data-testid={`history-badge-${r.reminder_id}`}>
            {badge.label}
          </span>
        </div>
        <span className="text-xs text-zinc-500 font-mono">{Math.round(r.trigger_miles_since)} / {r.interval} mi</span>
      </div>
      {r.snoozed && r.snooze_until_date && (
        <div className="mt-1.5 text-[11px] text-zinc-500">
          Snoozed until {r.snooze_until_date.slice(0, 10)} <span className="text-zinc-600">or</span> {Math.round(r.snooze_until_miles).toLocaleString()} mi
        </div>
      )}
    </div>
  );
}



// ============================================================
// Receipt Vault sub-components
// ============================================================

/** Pill badge for a receipt's category. Falls back to the "other" style for
 *  any unknown value so a legacy/migrated receipt never blows up the layout. */
function CategoryBadge({ category }) {
  const meta = CATEGORY_BY_VALUE[category] || CATEGORY_BY_VALUE.other;
  return (
    <span
      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border font-mono ${meta.color}`}
      data-testid={`receipt-category-${category}`}
    >
      {meta.label}
    </span>
  );
}

function ReceiptsTab({ receipts, stats, loading, filter, setFilter, categoryFilter, setCategoryFilter, onAdd, onOpenDetail, onExport }) {
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-6" data-testid="receipts-tab">
      {/* Summary card */}
      <div className="border border-white/10 bg-gradient-to-br from-[#FF3B30]/8 to-transparent p-6" data-testid="receipts-summary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-zinc-500">RECEIPT VAULT</div>
            <div className="font-display text-3xl mt-1" data-testid="receipts-total-amount">{fmtMoney(stats.total)}</div>
            <div className="text-sm text-zinc-400 mt-1">
              {stats.count || 0} receipt{stats.count === 1 ? "" : "s"} · {fmtMoney(stats.this_month)} this month · {fmtMoney(stats.this_year)} this year
            </div>
          </div>
          <button
            onClick={onExport}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="receipts-export-btn"
            title="Includes mileage log + maintenance receipts"
          >
            <FileDown className="w-4 h-4" /> Export Tax Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex gap-2 flex-wrap" data-testid="receipt-filters">
          {RECEIPT_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-display tracking-widest transition-colors uppercase ${filter === f.value ? "bg-[#FF3B30] text-white" : "border border-white/10 text-zinc-400 hover:text-white"}`}
              data-testid={`receipt-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="receipt-category-filters">
          <button
            onClick={() => setCategoryFilter("")}
            className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${categoryFilter === "" ? "border-white text-white" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}
            data-testid="receipt-cat-all"
          >
            All Categories
          </button>
          {RECEIPT_CATEGORIES.map((c) => (
            <button
              key={c.value}
              onClick={() => setCategoryFilter(categoryFilter === c.value ? "" : c.value)}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 border font-mono transition-colors ${categoryFilter === c.value ? c.color + " border-current" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}
              data-testid={`receipt-cat-${c.value}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add button */}
      <button
        onClick={onAdd}
        className="w-full border-2 border-dashed border-white/15 hover:border-[#FF3B30]/50 hover:bg-[#FF3B30]/5 transition-colors py-6 flex items-center justify-center gap-2 text-zinc-300 hover:text-white"
        data-testid="receipt-add-btn"
      >
        <Plus className="w-5 h-5" /> <span className="font-display tracking-widest text-sm">ADD RECEIPT</span>
      </button>

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-zinc-500 label-eyebrow">LOADING RECEIPTS…</div>
      ) : receipts.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="receipts-empty">
          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <div className="font-display text-xl text-white">No receipts yet</div>
          <div className="text-sm mt-1">Snap a photo of your next service or repair invoice to start your tax file.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="receipts-list">
          {receipts.map((r) => (
            <ReceiptRow key={r.receipt_id} receipt={r} onOpen={() => onOpenDetail(r)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReceiptRow({ receipt, onOpen }) {
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-[#121214] border border-white/10 hover:border-white/30 p-3 flex items-center gap-3 transition-colors"
      data-testid={`receipt-row-${receipt.receipt_id}`}
    >
      <ReceiptPhotoThumb path={receipt.photo_path} className="w-14 h-14 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Calendar className="w-3 h-3" /> {receipt.date}
          <CategoryBadge category={receipt.category} />
        </div>
        <div className="font-display text-base text-white mt-1 truncate">{receipt.shop_name}</div>
        {receipt.notes && <div className="text-xs text-zinc-500 mt-0.5 truncate">{receipt.notes}</div>}
      </div>
      <div className="text-right font-display text-lg shrink-0">{fmtMoney(receipt.amount)}</div>
    </button>
  );
}

/** Renders a thumbnail of a stored receipt photo using AuthImage which
 *  fetches through a blob URL with proper Authorization headers — avoids
 *  the query-string token that could silently fail and produce a black image. */
function ReceiptPhotoThumb({ path, className = "" }) {
  if (!path) {
    return (
      <div className={`bg-[#09090B] border border-white/10 flex items-center justify-center text-zinc-600 ${className}`}>
        <ImageIcon className="w-5 h-5" />
      </div>
    );
  }
  return (
    <div className={`bg-[#09090B] border border-white/10 overflow-hidden ${className}`}>
      <AuthImage path={path} alt="receipt" className="w-full h-full object-cover" />
    </div>
  );
}

function ReceiptForm({ editing, uploadPhoto, onSave, onClose }) {
  const isEdit = !!editing;
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPath, setPhotoPath] = useState(editing?.photo_path || "");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [date, setDate] = useState(editing?.date || new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(editing?.amount != null ? String(editing.amount) : "");
  const [shopName, setShopName] = useState(editing?.shop_name || "");
  const [category, setCategory] = useState(editing?.category || "oil_change");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Esc-to-close — consistent with the maint-settings + export modals.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Guard: some mobile cameras produce a 0-byte file → would render as a
    // black image. Reject early so the user can retake.
    if (f.size === 0) {
      toast.error("Photo appears empty — please retake");
      e.target.value = "";
      return;
    }
    setPhotoFile(f);
    setPhotoPath("");
    // Preview via FileReader so the user sees the photo immediately, even
    // before the upload to storage completes.
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (!ev.target.result || ev.target.result === "data:") {
        toast.error("Could not read photo — please try again");
        setPhotoFile(null);
        return;
      }
      setPhotoPreview(ev.target.result);
    };
    reader.onerror = () => {
      toast.error("Failed to read photo file");
      setPhotoFile(null);
    };
    reader.readAsDataURL(f);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!photoPath && !photoFile) { toast.error("Please add a photo of the receipt"); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!shopName.trim()) { toast.error("Shop name is required"); return; }
    setSaving(true);
    try {
      let path = photoPath;
      if (photoFile) {
        setUploading(true);
        path = await uploadPhoto(photoFile);
        setUploading(false);
        if (!path) throw new Error("Photo upload returned no path");
      }
      await onSave({
        photo_path: path,
        date,
        amount: amt,
        shop_name: shopName.trim(),
        category,
        notes: notes.trim() || null,
      });
    } catch (err) {
      toast.error(detailMsg(err, "Failed to save receipt"));
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="receipt-form-modal">
      <form onSubmit={handleSubmit} className="bg-[#121214] border border-white/10 w-full max-w-md p-6 sm:p-8 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-zinc-500">{isEdit ? "EDIT RECEIPT" : "NEW RECEIPT"}</div>
            <h2 className="font-display text-2xl mt-1">{isEdit ? "Update receipt" : "Save a maintenance receipt"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white p-1" data-testid="receipt-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Photo picker */}
        <div>
          <label className="label-eyebrow text-zinc-500 block mb-2">PHOTO *</label>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full aspect-[4/3] border-2 border-dashed border-white/15 hover:border-white/30 bg-[#09090B] flex items-center justify-center overflow-hidden relative"
            data-testid="receipt-photo-picker"
          >
            {photoPreview || photoPath ? (
              photoPreview ? (
                <img src={photoPreview} alt="receipt preview" className="w-full h-full object-cover" />
              ) : (
                <ReceiptPhotoThumb path={photoPath} className="w-full h-full" />
              )
            ) : (
              <div className="text-zinc-400 flex flex-col items-center gap-2">
                <Camera className="w-8 h-8" />
                <span className="text-xs uppercase tracking-widest">Tap to take or pick a photo</span>
              </div>
            )}
            {(photoPreview || photoPath) && !uploading && (
              <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] uppercase tracking-widest px-2 py-1">Tap to replace</span>
            )}
            {uploading && (
              <span className="absolute inset-0 bg-black/60 flex items-center justify-center label-eyebrow text-white">UPLOADING…</span>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPickFile} className="hidden" data-testid="receipt-photo-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow text-zinc-500">DATE *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required
                   className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="receipt-date-input" />
          </div>
          <div>
            <label className="label-eyebrow text-zinc-500">AMOUNT *</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required
                     placeholder="0.00" className="w-full bg-[#09090B] border border-white/10 pl-7 pr-3 py-2 text-white" data-testid="receipt-amount-input" />
            </div>
          </div>
        </div>

        <div>
          <label className="label-eyebrow text-zinc-500">SHOP NAME *</label>
          <input value={shopName} onChange={(e) => setShopName(e.target.value)} required maxLength={120}
                 placeholder="e.g. Jiffy Lube, Walmart Auto" className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="receipt-shop-input" />
        </div>

        <div>
          <label className="label-eyebrow text-zinc-500">CATEGORY *</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="receipt-category-select">
            {RECEIPT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="label-eyebrow text-zinc-500">NOTES</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500}
                    placeholder="Optional — what work was done?" className="w-full bg-[#09090B] border border-white/10 px-3 py-2 mt-1 text-white" data-testid="receipt-notes-input" />
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <button type="button" onClick={onClose} disabled={saving} className="flex-1 py-3 border border-white/10 text-zinc-300 font-display tracking-wide" data-testid="receipt-form-cancel">
            CANCEL
          </button>
          <button type="submit" disabled={saving || uploading} className="flex-1 py-3 bg-[#FF3B30] text-white font-display tracking-wide disabled:opacity-60" data-testid="receipt-form-save">
            {saving ? "SAVING…" : isEdit ? "UPDATE" : "SAVE RECEIPT"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReceiptDetailModal({ receipt, onClose, onEdit, onDelete }) {
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="receipt-detail-modal">
      <div className="bg-[#121214] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="label-eyebrow text-zinc-500">RECEIPT</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="receipt-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ReceiptPhotoThumb path={receipt.photo_path} className="w-full aspect-[4/3] !border-0" />
        <div className="p-5 space-y-4">
          <div>
            <div className="font-display text-3xl">{fmtMoney(receipt.amount)}</div>
            <div className="text-sm text-zinc-400 mt-1">{receipt.shop_name}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
              <Calendar className="w-3 h-3" /> {receipt.date}
              <CategoryBadge category={receipt.category} />
            </div>
          </div>
          {receipt.notes && (
            <div className="bg-[#09090B] border border-white/10 p-3 text-sm text-zinc-300">
              {receipt.notes}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={onEdit} className="flex-1 py-2.5 border border-white/10 text-zinc-200 font-display tracking-wide flex items-center justify-center gap-2" data-testid="receipt-edit-btn">
              <Edit2 className="w-4 h-4" /> EDIT
            </button>
            <button onClick={onDelete} className="flex-1 py-2.5 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 font-display tracking-wide flex items-center justify-center gap-2" data-testid="receipt-delete-btn">
              <Trash2 className="w-4 h-4" /> DELETE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Premium paywall (shared by Receipts and Profit Radar tabs)
// ============================================================
function PremiumPaywall({ testId, icon: Icon, title, body, priceLabel }) {
  return (
    <div
      className="border border-[#FF9500]/30 bg-gradient-to-br from-[#FF9500]/8 to-transparent p-8 sm:p-12 text-center"
      data-testid={testId}
    >
      <div className="w-14 h-14 mx-auto bg-[#FF9500]/15 flex items-center justify-center mb-5">
        <Icon className="w-6 h-6 text-[#FF9500]" />
      </div>
      <div className="label-eyebrow text-[#FF9500] mb-3 inline-flex items-center gap-2"><Lock className="w-4 h-4" /> PREMIUM FEATURE</div>
      <h2 className="font-display text-2xl sm:text-3xl mb-3">{title}</h2>
      <p className="text-zinc-400 max-w-md mx-auto leading-relaxed">{body}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-300">
        <Crown className="w-4 h-4 text-[#FF9500]" />
        Premium tracks your miles <span className="font-semibold text-white">and</span> your money — Auto-Track, receipts, profit breakdown, the whole picture.
      </div>
      <div className="mt-7">
        <Link to="/owner/upgrade" className="btn-primary inline-flex items-center gap-2" data-testid={`${testId}-upgrade`}>
          <Crown className="w-4 h-4" /> Upgrade to Premium — {priceLabel}
        </Link>
        <div className="text-[11px] text-zinc-500 mt-2">7-day free trial included.</div>
      </div>
    </div>
  );
}

// ============================================================
// Profit Radar — Premium dashboard
// ============================================================
function ProfitRadarTab() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [period, setPeriod] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [paywallHit, setPaywallHit] = useState(false);

  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const load = async () => {
    setLoading(true);
    try {
      // Fetch settings FIRST — if 402, show paywall immediately
      let settingsRes;
      try {
        settingsRes = await api.get("/profit-radar/settings");
      } catch (e) {
        if (e?.response?.status === 402) {
          setPaywallHit(true);
          setLoading(false);
          return;
        }
        throw e;
      }
      setSettings(settingsRes.data);

      // Check if needs_setup (all values are 0)
      const s = settingsRes.data;
      if (s.needs_setup || (s.average_mpg === 0 && s.local_gas_price === 0 && s.tax_percentage === 0)) {
        setSetupRequired(true);
        setLoading(false);
        return;
      }

      // Only fetch shifts + summary if settings succeeded
      const [sh, sum] = await Promise.all([
        api.get("/profit-radar/shifts"),
        api.get(`/profit-radar/summary?period=${period}`),
      ]);
      setShifts(sh.data?.shifts || []);
      setSummary(sum.data);
    } catch (e) {
      toast.error(detailMsg(e, "Failed to load Profit Radar"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async (payload) => {
    try {
      const r = await api.post("/profit-radar/settings", payload);
      setSettings(r.data);
      setSettingsOpen(false);
      setSetupRequired(false);
      toast.success("Saved. Future shifts will use these numbers.");
      await load();
    } catch (e) {
      toast.error(detailMsg(e, "Save failed"));
    }
  };

  const logShift = async (payload) => {
    try {
      await api.post("/profit-radar/shifts", payload);
      setLogOpen(false);
      toast.success("Shift logged");
      await load();
    } catch (e) {
      toast.error(detailMsg(e, "Save failed"));
    }
  };

  const deleteShift = async (id) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await api.delete(`/profit-radar/shifts/${id}`);
      await load();
    } catch (e) { toast.error(e?.message || "Delete failed"); }
  };

  // --- Paywall screen ---
  if (paywallHit) {
    return (
      <div className="text-center py-16 space-y-4" data-testid="pr-paywall">
        <Crown className="w-10 h-10 text-[#FF9500] mx-auto" />
        <h2 className="font-display text-2xl">Profit Radar is Premium</h2>
        <p className="text-sm text-zinc-400 max-w-sm mx-auto">
          See what you really make after gas and taxes. Upgrade to unlock your true numbers.
        </p>
        <button onClick={() => navigate("/upgrade")} className="btn-primary px-8 py-3 mx-auto" data-testid="pr-upgrade-btn">
          <Crown className="w-4 h-4 inline -mt-0.5 mr-1" /> UPGRADE
        </button>
      </div>
    );
  }

  // --- Mandatory setup modal when needs_setup ---
  if (setupRequired) {
    return (
      <div className="space-y-6" data-testid="pr-setup-required">
        <div className="border border-[#FF9500]/40 bg-[#FF9500]/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-[#FF9500] mx-auto mb-3" />
          <h2 className="font-display text-xl">Set Up Profit Radar</h2>
          <p className="text-sm text-zinc-300 mt-2 max-w-sm mx-auto">
            Enter your car's MPG, local gas price, and tax rate so we can show you real numbers.
          </p>
          <button onClick={() => setSettingsOpen(true)} className="btn-primary mt-4 px-6 py-3" data-testid="pr-setup-btn">
            ENTER MY NUMBERS
          </button>
        </div>
        {settingsOpen && (
          <ProfitRadarSettingsModal
            initial={settings || { average_mpg: 0, local_gas_price: 0, tax_percentage: 0 }}
            onClose={() => setSettingsOpen(false)}
            onSave={saveSettings}
          />
        )}
      </div>
    );
  }

  if (loading && !summary) {
    return <div className="text-center text-zinc-500 py-12 label-eyebrow">LOADING PROFIT RADAR…</div>;
  }

  // --- Empty state: premium but 0 shifts ---
  if (shifts.length === 0 && !loading) {
    return (
      <div className="space-y-6" data-testid="profit-radar-tab">
        <TrueClock stats={null} onShiftEnd={logShift} />
        <div className="border border-white/10 bg-[#121214] p-8 text-center">
          <Sparkles className="w-8 h-8 text-[#FF9500] mx-auto mb-3" />
          <h3 className="font-display text-lg">Log your first shift to see stats</h3>
          <p className="text-sm text-zinc-400 mt-2">Use the True Clock above or tap the button below to enter a past shift.</p>
          <button onClick={() => setLogOpen(true)} className="btn-primary mt-4 px-6 py-3" data-testid="pr-first-shift-btn">
            <Plus className="w-4 h-4 inline -mt-0.5 mr-1" /> Log a Shift
          </button>
        </div>
        {logOpen && <LogShiftModal onClose={() => setLogOpen(false)} onSave={logShift} />}
        {settingsOpen && settings && <ProfitRadarSettingsModal initial={settings} onClose={() => setSettingsOpen(false)} onSave={saveSettings} />}
      </div>
    );
  }

  const takeHomePositive = (summary?.total_take_home || 0) >= 0;
  return (
    <div className="space-y-6" data-testid="profit-radar-tab">
      <TrueClock stats={null} onShiftEnd={logShift} />

      <div className="border border-white/10 bg-gradient-to-br from-[#FF3B30]/8 to-transparent p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow text-[#FF3B30]">PROFIT RADAR</div>
            <p className="text-sm text-zinc-300 mt-1 leading-relaxed max-w-lg">
              See what you <span className="font-semibold text-white">really</span> make after gas and taxes. No sugarcoating — just the estimate truth.
            </p>
          </div>
          <button onClick={() => setSettingsOpen(true)} className="text-xs text-zinc-400 hover:text-white border border-white/10 px-2 py-1 uppercase tracking-widest" data-testid="pr-settings-btn">
            Settings
          </button>
        </div>
      </div>

      <div className="flex gap-2" data-testid="pr-period-switcher">
        {[["daily", "Today"], ["weekly", "Week"], ["monthly", "Month"], ["all", "All Time"]].map(([k, label]) => (
          <button key={k} onClick={() => setPeriod(k)}
                  className={`px-3 py-1.5 text-xs font-display tracking-widest uppercase ${period === k ? "bg-[#FF3B30] text-white" : "border border-white/10 text-zinc-400 hover:text-white"}`}
                  data-testid={`pr-period-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <div className={`border p-6 ${takeHomePositive ? "border-[#34C759]/40 bg-[#34C759]/8" : "border-[#FF3B30]/40 bg-[#FF3B30]/8"}`} data-testid="pr-takehome-card">
        <div className="label-eyebrow text-zinc-500">REAL TAKE-HOME (after gas + taxes)</div>
        <div className={`font-display text-4xl sm:text-5xl mt-2 ${takeHomePositive ? "text-[#34C759]" : "text-[#FF3B30]"}`} data-testid="pr-takehome-value">
          {fmtMoney(summary?.total_take_home)}
        </div>
        <div className="text-sm text-zinc-400 mt-1">
          {fmtMoney(summary?.per_hour)}/hr · {summary?.shift_count || 0} shift{summary?.shift_count === 1 ? "" : "s"} · {Number(summary?.total_hours || 0).toFixed(1)} hr
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="border border-white/10 bg-[#121214] p-4" data-testid="pr-gross-card">
          <div className="flex items-center gap-2 text-zinc-500 label-eyebrow"><DollarSign className="w-3 h-3" /> GROSS</div>
          <div className="font-display text-xl mt-2">{fmtMoney(summary?.total_gross)}</div>
        </div>
        <div className="border border-white/10 bg-[#121214] p-4" data-testid="pr-gas-card">
          <div className="flex items-center gap-2 text-zinc-500 label-eyebrow"><Fuel className="w-3 h-3" /> GAS</div>
          <div className="font-display text-xl mt-2 text-[#FF9500]">−{fmtMoney(summary?.total_gas)}</div>
          {settings && <div className="text-[10px] text-zinc-500 mt-1">${settings.local_gas_price}/gal · {settings.average_mpg} mpg</div>}
        </div>
        <div className="border border-white/10 bg-[#121214] p-4" data-testid="pr-taxes-card">
          <div className="flex items-center gap-2 text-zinc-500 label-eyebrow"><Wallet className="w-3 h-3" /> TAXES</div>
          <div className="font-display text-xl mt-2 text-[#FF9500]">−{fmtMoney(summary?.total_taxes)}</div>
          {settings && <div className="text-[10px] text-zinc-500 mt-1">{settings.tax_percentage}% set aside</div>}
        </div>
      </div>

      <button onClick={() => setLogOpen(true)} className="btn-primary w-full" data-testid="pr-log-shift-btn">
        <Plus className="w-4 h-4 inline -mt-0.5 mr-1" /> Log a Shift
      </button>

      <div className="border border-white/10 bg-[#121214]">
        <div className="px-5 py-3 border-b border-white/10 label-eyebrow text-zinc-500">SHIFT HISTORY</div>
        {shifts.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No shifts logged yet — tap "Log a Shift" above to start.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {shifts.map((s) => (
              <div key={s.shift_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`pr-shift-${s.shift_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-zinc-500">{s.date}</div>
                  <div className="font-display text-base mt-0.5">
                    {fmtMoney(s.gross_earnings)} <span className="text-zinc-500 text-xs">gross · {s.hours_worked}h · {s.miles_driven}mi</span>
                  </div>
                  {s.notes && <div className="text-[11px] text-zinc-500 truncate mt-0.5">{s.notes}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-display text-base ${s.real_take_home >= 0 ? "text-[#34C759]" : "text-[#FF3B30]"}`}>{fmtMoney(s.real_take_home)}</div>
                  <div className="text-[10px] text-zinc-500">{fmtMoney(s.per_hour)}/hr</div>
                </div>
                <button onClick={() => deleteShift(s.shift_id)} className="text-zinc-600 hover:text-[#FF3B30]" data-testid={`pr-delete-${s.shift_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {settingsOpen && settings && <ProfitRadarSettingsModal initial={settings} onClose={() => setSettingsOpen(false)} onSave={saveSettings} />}
      {logOpen && <LogShiftModal onClose={() => setLogOpen(false)} onSave={logShift} />}
    </div>
  );
}

function ProfitRadarSettingsModal({ initial, onClose, onSave }) {
  const [mpg, setMpg] = useState(String(initial.average_mpg));
  const [gas, setGas] = useState(String(initial.local_gas_price));
  const [tax, setTax] = useState(String(initial.tax_percentage));
  const submit = () => {
    const m = parseFloat(mpg), g = parseFloat(gas), t = parseFloat(tax);
    if (!m || m <= 0) return toast.error("MPG must be > 0");
    if (!g || g <= 0) return toast.error("Gas price must be > 0");
    if (isNaN(t) || t < 0 || t > 60) return toast.error("Tax % must be 0–60");
    onSave({ average_mpg: m, local_gas_price: g, tax_percentage: t, display_mode: "hour" });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="pr-settings-modal">
      <div className="bg-[#121214] border border-white/10 w-full max-w-md p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-zinc-500">PROFIT RADAR</div>
            <h2 className="font-display text-2xl mt-1">Your numbers</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="pr-settings-close"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">CAR AVG MPG</label>
          <input type="number" inputMode="decimal" step="0.1" value={mpg} onChange={(e) => setMpg(e.target.value)} className="input-base w-full mt-1" data-testid="pr-mpg" />
          <div className="text-[11px] text-zinc-500 mt-1">Check your manual or Google "[car model] MPG"</div>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">GAS PRICE / GAL</label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
            <input type="number" inputMode="decimal" step="0.01" value={gas} onChange={(e) => setGas(e.target.value)} className="input-base w-full pl-7" data-testid="pr-gas-price" />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Use your last fill-up price</div>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">TAX % TO SET ASIDE</label>
          <div className="relative mt-1">
            <input type="number" inputMode="decimal" step="0.5" value={tax} onChange={(e) => setTax(e.target.value)} className="input-base w-full pr-7" data-testid="pr-tax-pct" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Self-employed? 25–30% is a safe estimate.</div>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-3 border border-white/10 text-zinc-300 font-display tracking-wide" data-testid="pr-settings-cancel">CANCEL</button>
          <button onClick={submit} className="flex-1 py-3 bg-[#FF3B30] text-white font-display tracking-wide" data-testid="pr-settings-save">SAVE</button>
        </div>
      </div>
    </div>
  );
}

function LogShiftModal({ onClose, onSave }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [gross, setGross] = useState("");
  const [hours, setHours] = useState("");
  const [miles, setMiles] = useState("");
  const [notes, setNotes] = useState("");
  const submit = () => {
    const g = parseFloat(gross), h = parseFloat(hours), m = parseFloat(miles);
    if (!g || g < 0) return toast.error("Enter gross earnings");
    if (!h || h <= 0) return toast.error("Hours worked must be > 0");
    if (isNaN(m) || m < 0) return toast.error("Miles must be ≥ 0");
    onSave({ date, gross_earnings: g, hours_worked: h, miles_driven: m, notes: notes.trim() || null });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="pr-log-modal">
      <div className="bg-[#121214] border border-white/10 w-full max-w-md p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-zinc-500">LOG A SHIFT</div>
            <h2 className="font-display text-2xl mt-1">How'd today go?</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="pr-log-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow text-zinc-500">DATE</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-base w-full mt-1" data-testid="pr-log-date" />
          </div>
          <div>
            <label className="label-eyebrow text-zinc-500">GROSS</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <input type="number" inputMode="decimal" step="0.01" value={gross} onChange={(e) => setGross(e.target.value)} className="input-base w-full pl-7" data-testid="pr-log-gross" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow text-zinc-500">HOURS</label>
            <input type="number" inputMode="decimal" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} className="input-base w-full mt-1" data-testid="pr-log-hours" />
          </div>
          <div>
            <label className="label-eyebrow text-zinc-500">MILES</label>
            <input type="number" inputMode="decimal" step="0.1" value={miles} onChange={(e) => setMiles(e.target.value)} className="input-base w-full mt-1" data-testid="pr-log-miles" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">NOTES (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={300} placeholder="Anything memorable? Busy night, dead zone, etc." className="input-base w-full mt-1" data-testid="pr-log-notes" />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-3 border border-white/10 text-zinc-300 font-display tracking-wide" data-testid="pr-log-cancel">CANCEL</button>
          <button onClick={submit} className="flex-1 py-3 bg-[#FF3B30] text-white font-display tracking-wide" data-testid="pr-log-save">SAVE SHIFT</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// True Clock — manual shift timer (Premium / Profit Radar)
// ============================================================
//
// Persists `tc_active_shift` = ISO start_time in localStorage so the timer
// survives backgrounding, screen lock, refresh, or full app restart. On
// re-open we detect the running shift and offer "End now" or "Discard".
//
// We deliberately don't poll the backend during the shift — the timer
// is purely client-side until "End Shift" confirms and submits.
const TC_LS_KEY = "mc_truclock_active_start";

function TrueClock({ onShiftEnd }) {
  // Validate stored start value on mount — discard if NaN or >24h old
  const [startAt, setStartAt] = useState(() => {
    const stored = localStorage.getItem(TC_LS_KEY);
    if (!stored) return null;
    const t = new Date(stored).getTime();
    if (isNaN(t)) {
      localStorage.removeItem(TC_LS_KEY);
      return null;
    }
    // Discard if >24h old (stale / orphaned shift)
    if (Date.now() - t > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(TC_LS_KEY);
      return null;
    }
    return stored;
  });
  const [tick, setTick] = useState(0);
  const [endingShift, setEndingShift] = useState(null); // { start, end, durationMin }
  const [stats, setStats] = useState(null);

  // 1Hz tick only while a shift is active. Depend on [startAt] so timer
  // re-fires when shift starts (fixes missing dependency).
  useEffect(() => {
    if (!startAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startAt]);

  // Lazy-load history stats
  const loadStats = async () => {
    try {
      const r = await api.get("/profit-radar/stats");
      setStats(r.data);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { loadStats(); }, []);

  const start = (e) => {
    e.stopPropagation();
    const iso = new Date().toISOString();
    localStorage.setItem(TC_LS_KEY, iso);
    setStartAt(iso);
    toast.success("Shift started. Drive safe.");
  };

  const end = (e) => {
    e.stopPropagation();
    if (!startAt) return;
    const startDate = new Date(startAt);
    const endDate = new Date();
    const durationMin = Math.max(1, Math.round((endDate - startDate) / 60000));
    setEndingShift({ start: startDate, end: endDate, durationMin });
  };

  const discard = (e) => {
    e.stopPropagation();
    if (!window.confirm("Discard this shift? Time tracked will be lost.")) return;
    localStorage.removeItem(TC_LS_KEY);
    setStartAt(null);
    setEndingShift(null);
  };

  const handleEnd = async (payload) => {
    const finalPayload = {
      ...payload,
      start_time: endingShift.start.toISOString(),
      end_time: endingShift.end.toISOString(),
    };
    await onShiftEnd(finalPayload);
    localStorage.removeItem(TC_LS_KEY);
    setStartAt(null);
    setEndingShift(null);
    loadStats();
  };

  const elapsed = startAt ? Math.max(0, Math.floor((Date.now() - new Date(startAt).getTime()) / 1000)) : 0;
  const hh = String(Math.floor(elapsed / 3600)).padStart(2, "0");
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  const rateTone = (r) => r >= 15 ? "text-[#34C759]" : r >= 10 ? "text-[#FF9500]" : "text-[#FF3B30]";

  return (
    <>
      <div className="border border-white/10 bg-gradient-to-br from-[#FF3B30]/10 to-[#FF9500]/5 p-6" data-testid="true-clock-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-eyebrow text-[#FF3B30]">THE TRUE CLOCK</div>
            <p className="text-xs text-zinc-400 mt-1 max-w-md">
              Your real hourly rate. No sugarcoating. Including every minute you spent waiting.
            </p>
          </div>
        </div>

        {!startAt ? (
          <button
            onClick={start}
            className="mt-5 w-full py-5 min-h-[48px] min-w-[48px] bg-[#34C759]/15 hover:bg-[#34C759]/25 border border-[#34C759]/40 text-[#34C759] font-display tracking-widest text-lg transition-colors flex items-center justify-center gap-2"
            style={{ touchAction: "manipulation" }}
            data-testid="tc-start-btn"
          >
            <Play className="w-5 h-5" /> START SHIFT
          </button>
        ) : (
          <div className="mt-5 space-y-3">
            <div className="text-center py-4" data-testid="tc-timer-display">
              <div className="font-mono text-5xl sm:text-6xl tabular-nums">
                {hh}:{mm}:{ss}
              </div>
              <div className="label-eyebrow text-zinc-500 mt-2">ELAPSED</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={discard}
                className="py-3 min-h-[48px] min-w-[48px] border border-white/10 text-zinc-300 hover:text-white font-display tracking-widest text-sm"
                style={{ touchAction: "manipulation" }}
                data-testid="tc-discard-btn"
              >
                DISCARD
              </button>
              <button
                onClick={end}
                className="py-3 min-h-[48px] min-w-[48px] bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-display tracking-widest text-sm flex items-center justify-center gap-2"
                style={{ touchAction: "manipulation" }}
                data-testid="tc-end-btn"
              >
                <Square className="w-4 h-4" /> END SHIFT
              </button>
            </div>
          </div>
        )}

        {stats && stats.shift_count >= 1 && (
          <div className="mt-5 grid grid-cols-3 gap-2 text-center border-t border-white/10 pt-4" data-testid="tc-stats-strip">
            <div>
              <div className="label-eyebrow text-zinc-500">AVG</div>
              <div className={`font-display text-lg ${rateTone(stats.avg_hourly_rate)}`}>${Number(stats.avg_hourly_rate || 0).toFixed(2)}/hr</div>
            </div>
            <div>
              <div className="label-eyebrow text-zinc-500">BEST</div>
              <div className={`font-display text-lg ${rateTone(stats.best_shift_rate)}`}>${Number(stats.best_shift_rate || 0).toFixed(2)}/hr</div>
            </div>
            <div>
              <div className="label-eyebrow text-zinc-500">TREND</div>
              <div className="font-display text-lg" data-testid="tc-trend">
                {stats.trend_direction === "up" ? "↗" : stats.trend_direction === "down" ? "↘" : "→"}
                <span className="text-xs text-zinc-500 ml-1 uppercase tracking-widest">{stats.trend_direction}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {endingShift && (
        <EndShiftModal
          duration={endingShift.durationMin}
          shiftStart={endingShift.start.toISOString()}
          shiftEnd={endingShift.end.toISOString()}
          onClose={() => setEndingShift(null)}
          onSubmit={handleEnd}
        />
      )}
    </>
  );
}

function EndShiftModal({ duration, shiftStart, shiftEnd, onClose, onSubmit }) {
  const [gross, setGross] = useState("");
  const [miles, setMiles] = useState("");
  const [notes, setNotes] = useState("");
  const [autoFilling, setAutoFilling] = useState(false);

  const autoFillMiles = async () => {
    if (!shiftStart) return;
    setAutoFilling(true);
    try {
      // fetch trips for today (the shift date)
      const date = shiftStart.slice(0, 10); // YYYY-MM-DD
      const res = await api.get(`/mileage/trips?start_date=${date}&end_date=${date}`);
      const trips = res.data?.trips || res.data || [];
      // filter trips that overlap the shift window
      const shiftStartMs = new Date(shiftStart).getTime();
      const shiftEndMs   = new Date(shiftEnd).getTime();
      const overlapping  = trips.filter(t => {
        if (!t.start_time) return true; // manual trip, include it
        const ts = new Date(t.start_time).getTime();
        const te = t.end_time ? new Date(t.end_time).getTime() : ts;
        return ts <= shiftEndMs && te >= shiftStartMs;
      });
      const total = overlapping.reduce((sum, t) => sum + (parseFloat(t.distance_miles) || 0), 0);
      setMiles(total.toFixed(1));
      toast.success(`Auto-filled ${total.toFixed(1)} mi from ${overlapping.length} trip${overlapping.length !== 1 ? "s" : ""}`);
    } catch {
      toast.error("Could not load trips. Enter miles manually.");
    } finally {
      setAutoFilling(false);
    }
  };
  const hours = duration / 60;
  const hh = Math.floor(duration / 60);
  const mm = duration % 60;

  const submit = () => {
    const g = parseFloat(gross), m = parseFloat(miles);
    if (!g || g < 0) return toast.error("Enter gross earnings");
    if (isNaN(m) || m < 0) return toast.error("Miles must be ≥ 0");
    const hoursWorked = Math.max(0.01, Math.round(hours * 100) / 100);
    if (hoursWorked <= 0) return toast.error("Shift duration must be greater than 0");
    onSubmit({
      date: new Date().toISOString().slice(0, 10),
      gross_earnings: g,
      hours_worked: hoursWorked,
      miles_driven: m,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="tc-end-modal">
      <div className="bg-[#121214] border border-white/10 w-full max-w-md p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-zinc-500">END SHIFT</div>
            <h2 className="font-display text-2xl mt-1">Shift: {hh}h {mm}m</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="tc-end-close"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">HOW MUCH DID YOU GROSS?</label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
            <input type="number" inputMode="decimal" step="0.01" value={gross} onChange={(e) => setGross(e.target.value)}
                   className="input-base w-full pl-7" data-testid="tc-end-gross" autoFocus />
          </div>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">MILES DRIVEN</label>
          <div className="flex gap-2 mt-1">
            <input type="number" inputMode="decimal" step="0.1" value={miles} onChange={(e) => setMiles(e.target.value)}
                   className="input-base flex-1" data-testid="tc-end-miles" placeholder="0" />
            <button
              type="button"
              onClick={autoFillMiles}
              disabled={autoFilling}
              className="px-3 py-2 min-h-[48px] border border-[#FF9500]/50 text-[#FF9500] text-xs font-display tracking-widest hover:bg-[#FF9500]/10 disabled:opacity-40 whitespace-nowrap"
              style={{ touchAction: "manipulation" }}
              data-testid="tc-end-autofill"
            >
              {autoFilling ? "..." : "AUTO-FILL"}
            </button>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Pulls miles from your Mileage Tracker for this shift window.</div>
        </div>
        <div>
          <label className="label-eyebrow text-zinc-500">NOTES (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={300} className="input-base w-full mt-1" data-testid="tc-end-notes" />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-3 min-h-[48px] border border-white/10 text-zinc-300 font-display tracking-wide" style={{ touchAction: "manipulation" }} data-testid="tc-end-cancel">CANCEL</button>
          <button onClick={submit} className="flex-1 py-3 min-h-[48px] bg-[#FF3B30] text-white font-display tracking-wide" style={{ touchAction: "manipulation" }} data-testid="tc-end-save">SEE MY RATE</button>
        </div>
      </div>
    </div>
  );
}
